import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { hasError, isPersistable, isUpdating } from '@ublend-npm/aula-editor';
import { trimEnd } from '@ublend-npm/aula-editor-utils';

import { AulaEditorWrapper } from './AulaEditor.styles';

const withFileBlockValidation = (AulaEditor) => {
  const FileBlockValidation = ({
    onChange,
    onReturn,
    forwardedRef,
    setSubmittingState,
    isSubmitting,
    conversationId,
    config,
    editorState,
    ...props
  }) => {
    React.useEffect(() => {
      if (isSubmitting && !isUpdating(editorState, config)) {
        if (
          isPersistable(editorState, config) &&
          !hasError(editorState, config)
        ) {
          onReturn(trimEnd(editorState), conversationId);
        }
        if (setSubmittingState) {
          setSubmittingState(false);
        }
      }
    }, [
      isSubmitting,
      editorState,
      config,
      onReturn,
      conversationId,
      setSubmittingState,
    ]);

    const handleOnReturn = useCallback(
      (editorState) => {
        if (isUpdating(editorState, config)) {
          if (setSubmittingState) {
            setSubmittingState(true);
          }
        } else if (
          isPersistable(editorState, config) &&
          !hasError(editorState, config)
        ) {
          onReturn(trimEnd(editorState), conversationId);
        }
      },
      [config, conversationId, onReturn, setSubmittingState]
    );

    const handleOnChange = useCallback(
      (nextEditorState) => {
        onChange(nextEditorState, conversationId);

        // Deliver the message when the state is updated by the aula editor
        // and the submit is in progress until the state becomes persistable
        if (isSubmitting) {
          if (hasError(nextEditorState, config)) {
            if (setSubmittingState) {
              setSubmittingState(false);
            }
          } else if (!isUpdating(nextEditorState, config)) {
            if (isPersistable(nextEditorState, config)) {
              onReturn(nextEditorState, conversationId);
            }
            if (setSubmittingState) {
              setSubmittingState(false);
            }
          }
        }
      },
      [
        isSubmitting,
        config,
        conversationId,
        onChange,
        onReturn,
        setSubmittingState,
      ]
    );

    return (
      <AulaEditorWrapper readOnly={isSubmitting}>
        <AulaEditor
          onChange={handleOnChange}
          onReturn={handleOnReturn}
          readOnly={isSubmitting}
          ref={forwardedRef}
          config={config}
          editorState={editorState}
          {...props}
        />
      </AulaEditorWrapper>
    );
  };

  FileBlockValidation.propTypes = {
    onChange: PropTypes.func.isRequired,
    onReturn: PropTypes.func.isRequired,
    onFilesStatusChange: PropTypes.func,
    setSubmittingState: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    conversationId: PropTypes.string,
    config: PropTypes.shape({}),
    editorState: PropTypes.shape({}),
    forwardedRef: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    ]).isRequired,
  };

  FileBlockValidation.defaultProps = {
    config: undefined,
    editorState: undefined,
    conversationId: undefined,
    onFilesStatusChange: undefined,
  };

  return React.forwardRef((props, ref) => {
    return <FileBlockValidation {...props} forwardedRef={ref} />;
  });
};

export default withFileBlockValidation;
